import { Drawer } from 'expo-router/drawer';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { DrawerContentComponentProps } from '@react-navigation/drawer';
import { useMemo, useEffect } from 'react';
import { useRouter } from 'expo-router';
import { watchPush } from '../../services/pushService';
import { useWindowDimensions } from 'react-native';
import { CustomDrawerContent } from '../../components/CustomDrawerContent';
import { NavBackButton } from '../../components/NavBackButton';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { UserRole } from '../../types';

export default function AppLayout() {
  const { appUser } = useAuth();
  const router = useRouter();
  const pushUserId = appUser?.uid;
  const pushRole = appUser?.role;
  useEffect(() => {
    if (!pushUserId || pushRole === 'guest') return;
    return watchPush(() => router.push('/(app)/tasks'));
  }, [pushUserId, pushRole, router]);
  const { t } = useLanguage();
  const role: UserRole = appUser?.role ?? 'guest';
  const canManage = role === 'admin' || role === 'manager';
  const canView = role !== 'guest';
  const isAdmin = role === 'admin';
  const { width } = useWindowDimensions();
  const isDesktop = width >= 1024;

  const drawerScreenOptions = useMemo(
    () => ({
      headerShown: true,
      headerStyle: isDesktop ? { backgroundColor: '#0066CC', height: 56 } : { backgroundColor: '#0066CC' },
      headerTintColor: '#fff',
      drawerType: (isDesktop ? 'permanent' : 'front') as 'permanent' | 'front' | 'back' | 'slide',
      drawerStyle: isDesktop ? { width: 260, borderRightWidth: 0 } : undefined,
      overlayColor: isDesktop ? 'transparent' : undefined,
      swipeEnabled: !isDesktop,
      headerLeft: isDesktop ? () => null : undefined,
      headerShadowVisible: !isDesktop,
    }),
    [isDesktop],
  );

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <Drawer
        drawerContent={(props: DrawerContentComponentProps) => <CustomDrawerContent {...props} />}
        screenOptions={drawerScreenOptions}
      >
        <Drawer.Screen name="home" options={{ title: t('tabs.home') }} />
        <Drawer.Screen name="operations" options={{ drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="tasks" options={{ title: t('operations.title'), drawerItemStyle: canView ? undefined : {display: 'none'} }} />
        <Drawer.Screen name="stores" options={{ title: t('tabs.stores'), headerLeft: () => <NavBackButton /> }} />
        <Drawer.Screen
          name="categories"
          options={{ title: t('tabs.inventory'), drawerItemStyle: canView ? undefined : { display: 'none' }, headerLeft: () => <NavBackButton /> }}
        />
        <Drawer.Screen name="usage" options={{ title: t('tabs.usage'), headerLeft: () => <NavBackButton /> }} />
        <Drawer.Screen
          name="documents"
          options={{ title: t('tabs.documents'), drawerItemStyle: canView ? undefined : { display: 'none' }, headerLeft: () => <NavBackButton /> }}
        />
        <Drawer.Screen
          name="admin"
          options={{ title: t('tabs.admin'), drawerItemStyle: isAdmin ? undefined : { display: 'none' }, headerLeft: () => <NavBackButton /> }}
        />
        <Drawer.Screen
          name="admin-users"
          options={{ title: t('admin.userManagement'), drawerItemStyle: { display: 'none' }, headerLeft: () => <NavBackButton /> }}
        />
        <Drawer.Screen
          name="scheduler"
          options={{ title: t('admin.scheduler'), drawerItemStyle: { display: 'none' }, headerLeft: () => <NavBackButton /> }}
        />
        <Drawer.Screen
          name="audit-logs"
          options={{ title: t('tabs.auditLogs'), drawerItemStyle: isAdmin ? undefined : { display: 'none' }, headerLeft: () => <NavBackButton /> }}
        />
        <Drawer.Screen
          name="reports"
          options={{ title: t('tabs.reports'), drawerItemStyle: canManage ? undefined : { display: 'none' }, headerLeft: () => <NavBackButton /> }}
        />
        <Drawer.Screen
          name="contact"
          options={{ title: t('drawer.contactLabel'), drawerItemStyle: { display: 'none' }, headerLeft: () => <NavBackButton /> }}
        />
        <Drawer.Screen
          name="changelog"
          options={{ title: t('changelog.title'), drawerItemStyle: { display: 'none' }, headerLeft: () => <NavBackButton /> }}
        />
        <Drawer.Screen
          name="profile"
          options={{ title: t('tabs.profile'), drawerItemStyle: { display: 'none' }, headerLeft: () => <NavBackButton /> }}
        />
      </Drawer>
    </GestureHandlerRootView>
  );
}
